/**
 * The connection types the editor knows about, and the shape of each one's form.
 *
 * `layout` picks the page:
 *   "http" — the endpoint builder (method + URL + Parameters/Authentication/Headers/Body),
 *            with a module → endpoint tree in the sidebar.
 *   "db"   — the connection form (host / port / database / credentials), with the
 *            application's other connections of that type listed in the sidebar.
 *
 * `icon` and `resoueceType` are the values the API stores. They are duplicated
 * here as a fallback: the payload normally takes icon/category/description from
 * the /resource-type record, but the page must still work if that call has not
 * come back yet. Note `resoueceType` — the misspelling is the API's field name.
 */

/**
 * `category` and `description` are the /resource-type record's, repeated here as
 * the fallback used until that call returns — the antd forms read them straight
 * off `props.resourceType`, so a record saved here carries the same values.
 *
 * MySQL is not offered: it was in an early pass of the new editor, never in the
 * old flow, and there is no MySqlForm behind it. Records of that type, if any
 * exist, still list and still open.
 */
export const TYPE_REGISTRY = [
  { key: "restapi", label: "REST API", icon: "rest_api", resoueceType: "Rest API", layout: "http", category: "api" },
  { key: "graphql", label: "Graph QL", icon: "graphql", resoueceType: "GraphQL", layout: "http", category: "api" },
  { key: "mongodb", label: "Mongo DB", icon: "mongodb", resoueceType: "MongoDB", layout: "db", category: "database" },
];

/* A resource's type arrives two ways — `icon` ("rest_api") and `resoueceType`
   ("Rest Api") — and neither is consistent about spacing or case. Both squash to
   the same key. Same helper as the list page uses. */
export const typeKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export const findType = (key) => TYPE_REGISTRY.find((t) => t.key === typeKey(key)) || null;

/* ── HTTP form ───────────────────────────────────────────────── */

// export const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
/* GET and POST only — the original design, and all the antd RestApiForm and
   GraphQLForm ever offered. A report only ever reads data, so the write verbs
   had no use here. */
export const HTTP_METHODS = ["GET", "POST"];

/* The four types the saved model has always had, in the order the antd form
   lists them. Int and Float were tried here briefly and taken out again: nothing
   downstream has ever seen those values, so a row saved as "int" would have been
   a new shape for both the API and the report builders. */
export const DATA_TYPES = [
  { value: "number", label: "Number" },
  { value: "string", label: "String" },
  { value: "boolean", label: "Boolean" },
  { value: "date", label: "Date" },
];

/* Who a Witmeg API is offered to. Only meaningful for one — the payload carries
   visibleTo only when isWitmegApi is set, which is how the antd form sends it,
   and a global board's Resource dropdown selects on the pair of them. */
export const VISIBLE_TO_OPTIONS = [
  { value: "global", label: "Global" },
  { value: "internal", label: "Internal" },
];

/* ── Date parameters ─────────────────────────────────────────
   A row typed as Date takes no typed value. It carries a `dateConfig` instead,
   and the value is worked out when the report runs — so a report asking for
   "yesterday" still means yesterday a month from now.

   The option names, the stored keys and the default format are all the antd
   form's (DateConfigSelect.js), so a row written by either editor is read
   correctly by the other and by the five request builders that evaluate
   dateConfig at run time. */
export const DATE_OPTIONS = [
  { value: "fixedDate", label: "Fixed Date" },
  { value: "today", label: "Today" },
  { value: "daysBefore", label: "Days Before" },
];

export const DEFAULT_DATE_FORMAT = "YYYY-MM-DDTHH:mm:ss.SS[Z]";

/* Suggestions, not a closed list — the old form used an AutoComplete here, so a
   format that is not one of these has always been allowed. */
export const DATE_FORMATS = ["YYYY-MM-DD", DEFAULT_DATE_FORMAT, "YYYY-MM-DDThh:mm:ss.Z"];

/* Auth types, with the parameter keys the existing forms already store, so a
   resource saved here reopens correctly in the old modal and the other way
   round. "OAuth" is the design's name for what the API calls auth0. */
export const AUTH_TYPES = [
  { value: "none", label: "None", fields: [] },
  {
    value: "jwt",
    label: "JWT",
    fields: [
      { key: "aud", label: "AUD", placeholder: "Enter AUD value" },
      { key: "iss", label: "ISS", placeholder: "Enter ISS value" },
      { key: "privateKey", label: "Private Key", placeholder: "Paste private key", multiline: true },
    ],
  },
  {
    value: "basic",
    label: "Basic Auth",
    fields: [
      { key: "username", label: "Username", placeholder: "Enter username" },
      { key: "password", label: "Password", placeholder: "Enter password", secret: true },
    ],
  },
  {
    value: "auth0",
    label: "OAuth",
    fields: [
      { key: "domainName", label: "Domain", placeholder: "your-domain.auth0.com" },
      { key: "clientID", label: "Client ID", placeholder: "Enter client ID" },
      { key: "clientSecret", label: "Client Secret", placeholder: "Enter client secret", secret: true },
    ],
  },
];

export const findAuthType = (value) => AUTH_TYPES.find((a) => a.value === value) || AUTH_TYPES[0];

/* Body modes. Form Data and x-www-form-urlencoded are key/value tables; Raw is a
   text area with a format of its own. */
export const BODY_MODES = [
  { value: "none", label: "None" },
  { value: "formData", label: "Form Data" },
  { value: "urlencoded", label: "x-www-form-urlencoded" },
  { value: "raw", label: "Raw" },
];

export const RAW_FORMATS = [
  { value: "json", label: "JSON" },
  { value: "xml", label: "xml" },
  { value: "html", label: "html" },
  { value: "raw", label: "Raw" },
  { value: "javascript", label: "JavaScript" },
];

/* ── DB form ─────────────────────────────────────────────────── */

/* Every connection type shares one form; only these differ. `connectionMethods`
   is omitted for a type that has just one way to connect, and the radio row is
   then left out rather than shown with a single option. */
const DB_FIELDS = (defaultPort) => [
  { key: "name", label: "Name", placeholder: "Enter resource name...", required: true },
  { key: "host", label: "Host", placeholder: "Enter host", required: true },
  { key: "port", label: "Port", placeholder: defaultPort, defaultValue: defaultPort },
  { key: "databaseName", label: "DB Name", placeholder: "Enter db name" },
  { key: "databaseUsername", label: "Username", placeholder: "Enter username" },
  { key: "databasePassword", label: "Password", placeholder: "Enter password", secret: true },
];

export const DB_SCHEMAS = {
  mongodb: {
    connectionMethods: [
      { value: "standard", label: "Standard Connection" },
      { value: "dnsSeedList", label: "DNS Seed List Connection" },
    ],
    fields: DB_FIELDS("27017"),
  },
};

export const dbSchema = (key) => DB_SCHEMAS[typeKey(key)] || { fields: DB_FIELDS("") };
